import {
  Button,
  Input,
  TabList,
  Tab,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type { FacilityRow, OrganizationRow } from '@/api/types';
import { DataTable, EmptyState, ErrorState, LoadingState, type DataTableColumn } from '@/design-system';
import { spacing } from '@/theme/tokens';

import { useFacilities, useOrgs } from '../api';
import { buildPathWithMergedSearch } from '../navigation';

type DirectoryTab = 'organizations' | 'facilities';
const TAB_KEYS: readonly DirectoryTab[] = ['organizations', 'facilities'];
function isDirectoryTab(value: unknown): value is DirectoryTab {
  return typeof value === 'string' && (TAB_KEYS as readonly string[]).includes(value);
}

interface DirectoryProps {
  /** Initial tab — defaults to organizations. */
  initialTab?: DirectoryTab;
  /** Called when the user clicks the Users button (rendered alongside the TabList). The parent owns the UsersPanel. */
  onUsersClick?: () => void;
}

const useStyles = makeStyles({
  tabNav: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.s,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.l,
    paddingBlock: spacing.m,
    flexWrap: 'wrap',
  },
  search: {
    minWidth: '320px',
    maxWidth: '480px',
    flex: 1,
  },
  meta: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },

  clickableRow: {
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  // Single focusable action per row — wraps the row's primary identifier cell.
  // Keyboard users tab to this button; mouse users click anywhere on the row.
  rowAction: {
    color: tokens.colorBrandForeground1,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    fontSize: 'inherit',
    fontFamily: 'inherit',
    textAlign: 'left',
    ':hover': { textDecoration: 'underline' },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
      borderRadius: tokens.borderRadiusSmall,
    },
  },
});

const ROW_LIMIT = 200;

export function Directory({ initialTab = 'organizations', onUsersClick }: DirectoryProps) {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState<DirectoryTab>(initialTab);
  const [query, setQuery] = useState('');

  const orgs = useOrgs();
  const facilities = useFacilities();

  const filteredOrgs = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = orgs.data?.items ?? [];
    if (q.length === 0) {
      return all;
    }
    // `q` is lowercased; lowercase the haystack too so org IDs match
    // case-insensitively (`o.organizationId.includes(q)` alone would only
    // match all-lowercase IDs).
    return all.filter(
      (o) => o.displayName.toLowerCase().includes(q) || o.organizationId.toLowerCase().includes(q),
    );
  }, [orgs.data, query]);

  const filteredFacilities = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = facilities.data?.items ?? [];
    if (q.length === 0) {
      return all;
    }
    // `q` is lowercased; lowercase every haystack field too. Without this,
    // `f.facilityId.includes(q)` and `f.organizationId.includes(q)` would
    // miss IDs that contain uppercase characters.
    return all.filter(
      (f) =>
        f.displayName.toLowerCase().includes(q) ||
        f.facilityId.toLowerCase().includes(q) ||
        f.organizationId.toLowerCase().includes(q) ||
        (f.city ?? '').toLowerCase().includes(q) ||
        (f.state ?? '').toLowerCase().includes(q),
    );
  }, [facilities.data, query]);

  const visibleCount =
    tab === 'organizations' ? filteredOrgs.length : filteredFacilities.length;
  const total =
    tab === 'organizations' ? (orgs.data?.items.length ?? 0) : (facilities.data?.items.length ?? 0);
  const isCapped = tab === 'organizations' ? (orgs.data?.isCapped ?? false) : (facilities.data?.isCapped ?? false);

  const isLoading = tab === 'organizations' ? orgs.isLoading : facilities.isLoading;
  const isError = tab === 'organizations' ? orgs.isError : facilities.isError;

  return (
    <div>
      {/* Users is a drawer trigger, not a real tab panel. Rendering it outside
          the TabList avoids broken ARIA semantics (tab role requires a matching
          tabpanel). A subtle Button sits next to the TabList and opens the
          UsersPanel drawer when clicked. */}
      <div className={classes.tabNav}>
        <TabList
          size="medium"
          selectedValue={tab}
          onTabSelect={(_, data) => {
            // Guard the cast — Fluent's `data.value` is typed as `unknown` so a
            // future tab id mismatch becomes a no-op instead of a silent state
            // corruption.
            if (isDirectoryTab(data.value)) {
              setTab(data.value);
            }
          }}
          aria-label="Directory"
        >
          <Tab value="organizations">Organizations</Tab>
          <Tab value="facilities">Facilities</Tab>
        </TabList>
        {onUsersClick && (
          <Button
            appearance="subtle"
            onClick={() => { onUsersClick(); }}
            aria-haspopup="dialog"
            aria-label="View users"
          >
            Users
          </Button>
        )}
      </div>

      <div className={classes.toolbar}>
        <Input
          className={classes.search}
          contentBefore={<SearchRegular />}
          placeholder={
            tab === 'organizations'
              ? 'Search by name or org id'
              : 'Search by name, id, city, state'
          }
          value={query}
          onChange={(_, data) => {
            setQuery(data.value);
          }}
          aria-label={tab === 'organizations' ? 'Search organizations' : 'Search facilities'}
        />
        <Text className={classes.meta}>
          {isLoading ? (
            'Loading\u2026'
          ) : isError ? null : (
            <>
              {isCapped
                ? `Showing ${Math.min(visibleCount, ROW_LIMIT).toLocaleString()} of ${visibleCount.toLocaleString()} loaded results. The directory has more than ${total.toLocaleString()} records.`
                : <>
                    Showing {Math.min(visibleCount, ROW_LIMIT).toLocaleString()} of{' '}
                    {visibleCount.toLocaleString()}
                    {visibleCount > ROW_LIMIT
                      ? ` (first ${ROW_LIMIT.toString()} of ${total.toLocaleString()} total)`
                      : total !== visibleCount
                        ? ` (filtered from ${total.toLocaleString()})`
                        : ''}
                  </>}
            </>
          )}
        </Text>
      </div>

      {tab === 'organizations' ? (
        orgs.isLoading ? (
          <LoadingState label="Loading organizations…" />
        ) : orgs.isError ? (
          <ErrorState
            title="Couldn't load organizations"
            description={orgs.error.message}
            retry={() => void orgs.refetch()}
          />
        ) : (
          <OrgsTable
            rows={filteredOrgs}
            partial={orgs.data?.isCapped ?? false}
            onRowClick={(id) => {
              void navigate(
                buildPathWithMergedSearch(
                  `/business/organizations/${encodeURIComponent(id)}`,
                  location.search,
                ),
              );
            }}
          />
        )
      ) : facilities.isLoading ? (
        <LoadingState label="Loading facilities…" />
      ) : facilities.isError ? (
        <ErrorState
          title="Couldn't load facilities"
          description={facilities.error.message}
          retry={() => void facilities.refetch()}
        />
      ) : (
        <FacilitiesTable
          rows={filteredFacilities}
          partial={facilities.data?.isCapped ?? false}
          onRowClick={(row) => {
            void navigate(
              buildPathWithMergedSearch(
                `/business/facilities/${encodeURIComponent(row.facilityId)}`,
                location.search,
                { orgId: row.organizationId },
              ),
            );
          }}
        />
      )}
    </div>
  );
}

function OrgsTable({ rows, partial, onRowClick }: { rows: readonly OrganizationRow[]; partial: boolean; onRowClick: (id: string) => void }) {
  const classes = useStyles();
  const columns = useMemo<readonly DataTableColumn<OrganizationRow>[]>(() => [
    { id: 'name', header: 'Name', getValue: (item) => item.displayName, renderCell: (item) => <button type="button" className={classes.rowAction} onClick={(event) => { event.stopPropagation(); onRowClick(item.organizationId); }}>{item.displayName}</button>, width: 240, sortable: !partial },
    { id: 'id', header: 'ID', getValue: (item) => item.organizationId, renderCell: (item) => item.organizationId, width: 200, sortable: !partial },
    { id: 'facilities', header: 'Facilities', getValue: (item) => item.facilityCount, renderCell: (item) => item.facilityCount?.toLocaleString() ?? '—', sortable: !partial },
    { id: 'activeUsers', header: 'Active users', getValue: (item) => item.activeUserCount, renderCell: (item) => item.activeUserCount?.toLocaleString() ?? '—', sortable: !partial },
    { id: 'created', header: 'Created', getValue: (item) => item.createdAtEpochMs > 0 ? item.createdAtEpochMs : null, renderCell: (item) => item.createdAtEpochMs > 0 ? new Date(item.createdAtEpochMs).toLocaleDateString() : '—', sortable: !partial },
  ], [classes.rowAction, onRowClick, partial]);
  return <DataTable ariaLabel="Organizations" columns={columns} getRowId={(item) => item.organizationId} items={rows} tableId="directory-organizations" limit={ROW_LIMIT} sortDescription={partial ? 'Organization order follows the server order because this is a partial result.' : undefined} onRowClick={(item) => { onRowClick(item.organizationId); }} rowClassName={classes.clickableRow} emptyState={<EmptyState title="No organizations match the filter" description="Try a shorter or different search query." />} />;
}

function FacilitiesTable({ rows, partial, onRowClick }: { rows: readonly FacilityRow[]; partial: boolean; onRowClick: (row: FacilityRow) => void }) {
  const classes = useStyles();
  const columns = useMemo<readonly DataTableColumn<FacilityRow>[]>(() => [
    { id: 'name', header: 'Name', getValue: (item) => item.displayName, renderCell: (item) => <button type="button" className={classes.rowAction} onClick={(event) => { event.stopPropagation(); onRowClick(item); }}>{item.displayName}</button>, width: 240, sortable: !partial },
    { id: 'id', header: 'ID', getValue: (item) => item.facilityId, renderCell: (item) => item.facilityId, width: 200, sortable: !partial },
    { id: 'organization', header: 'Org', getValue: (item) => item.organizationDisplayName, renderCell: (item) => <>{item.organizationDisplayName}{item.organizationDisplayName !== item.organizationId ? <Text as="span" size={200} className={classes.meta}> ({item.organizationId})</Text> : null}</>, sortable: !partial },
    { id: 'city', header: 'City', getValue: (item) => item.city, renderCell: (item) => item.city ?? '—', sortable: !partial },
    { id: 'state', header: 'State', getValue: (item) => item.state, renderCell: (item) => item.state ?? '—', sortable: !partial },
    { id: 'activePatients', header: 'Active patients', getValue: (item) => item.activePatientCount, renderCell: (item) => item.activePatientCount?.toLocaleString() ?? '—', sortable: !partial },
  ], [classes.meta, classes.rowAction, onRowClick, partial]);
  return <DataTable ariaLabel="Facilities" columns={columns} getRowId={(item) => item.facilityId} items={rows} tableId="directory-facilities" limit={ROW_LIMIT} sortDescription={partial ? 'Facility order follows the server order because this is a partial result.' : undefined} onRowClick={onRowClick} rowClassName={classes.clickableRow} emptyState={<EmptyState title="No facilities match the filter" description="Try a shorter or different search query." />} />;
}
